/**
 * PeerConnection — minimal wrapper around RTCPeerConnection for 1-on-1 calls.
 *
 * Wires:
 *   • Local media (getUserMedia / getDisplayMedia) → outbound tracks
 *   • Inbound tracks from remote peer → callback (onRemoteStream)
 *   • SDP/ICE negotiation events → callback (onSignal) so the SignalingClient
 *     can relay them
 *
 * Designed so the host is the offerer (createOffer) and the guest answers,
 * but the negotiation flow handles either direction via standard "perfect
 * negotiation" patterns where helpful.
 *
 * Out of scope:
 *   • Reconnection / ICE-restart bookkeeping (Phase 1 polish week 3)
 *   • Adaptive bitrate / simulcast (only relevant when we move to an SFU)
 */
export class PeerConnection {
  /**
   * @param {Object} opts
   * @param {Array<RTCIceServer>} opts.iceServers
   * @param {(signal: {type: 'offer'|'answer'|'ice', payload: any}) => void} opts.onSignal
   * @param {(stream: MediaStream | null) => void} opts.onRemoteStream
   * @param {(state: RTCPeerConnectionState) => void} opts.onConnectionState
   * @param {boolean} opts.isOfferer  - true for host, false for guest
   */
  constructor({ iceServers, onSignal, onRemoteStream, onConnectionState, isOfferer }) {
    this.onSignal = onSignal || (() => {});
    this.onRemoteStream = onRemoteStream || (() => {});
    this.onConnectionState = onConnectionState || (() => {});
    this.isOfferer = !!isOfferer;
    // Perfect-negotiation roles: the host is "impolite" (its offers win),
    // the guest is "polite" (rolls back on collision). See
    // https://www.w3.org/TR/webrtc/#perfect-negotiation-example
    this._isPolite = !this.isOfferer;
    this._makingOffer = false;
    this._ignoreOffer = false;
    this.localStream = null;
    this.screenStream = null;
    this.remoteStream = new MediaStream();
    this._screenSenders = []; // RTCRtpSender[] for screen-share tracks (so we can stop them)

    this.pc = new RTCPeerConnection({
      iceServers,
      // Allow both host candidates (peer-to-peer) and relay (TURN). Forcing
      // `relay` would always work but eats more bandwidth — `all` lets the
      // browser pick the cheapest viable path.
      iceTransportPolicy: 'all',
      // Bundle audio + video on a single transport (default in unified-plan
      // but explicit so we don't accidentally regress on Safari).
      bundlePolicy: 'max-bundle',
    });

    this.pc.addEventListener('icecandidate', (event) => {
      if (event.candidate) {
        this.onSignal({ type: 'ice', payload: event.candidate.toJSON() });
      }
    });

    this.pc.addEventListener('track', (event) => {
      // Aggregate all remote tracks into a single MediaStream so consumers
      // can attach <video srcObject={remoteStream}/>
      event.streams.forEach((s) => {
        s.getTracks().forEach((t) => this._upsertRemote(t));
      });
      // Some browsers fire `track` without an attached stream (e.g. screen-only)
      if (!event.streams.length && event.track) {
        this._upsertRemote(event.track);
      }
      this.onRemoteStream(this.remoteStream);
    });

    this.pc.addEventListener('connectionstatechange', () => {
      this.onConnectionState(this.pc.connectionState);
      // Log ICE selected candidate when connected — invaluable for
      // diagnosing "no media flowing" issues. `relay` candidates indicate
      // the call is going through TURN; `host`/`srflx` mean direct/STUN.
      if (this.pc.connectionState === 'connected') {
        this._logSelectedIceCandidate().catch(() => {});
      }
    });

    this.pc.addEventListener('negotiationneeded', async () => {
      // Only the offerer initiates; the answerer waits for an offer.
      if (!this.isOfferer) return;
      // Re-entry / state guards: prevent the "m-line order doesn't match"
      // crash that happens when a second offer fires before the first
      // offer/answer round completes.
      if (this._makingOffer) return;
      if (this.pc.signalingState !== 'stable') return;
      try {
        this._makingOffer = true;
        await this._makeOffer();
      } catch (e) {
        // surfaced via connectionstatechange
      } finally {
        this._makingOffer = false;
      }
    });
  }

  _upsertRemote(track) {
    // Replace any existing track of the same kind so screen-share swaps work
    const existing = this.remoteStream.getTracks().find((t) => t.kind === track.kind && t.id === track.id);
    if (!existing) {
      this.remoteStream.addTrack(track);
    }
    track.addEventListener('ended', () => {
      try { this.remoteStream.removeTrack(track); } catch (e) { /* */ }
      this.onRemoteStream(this.remoteStream);
    });
  }

  /**
   * Acquire the local mic/cam stream and attach to the PeerConnection.
   *
   * iOS Safari notes:
   *  • Safari rejects the bare `{video: true, audio: true}` form less often
   *    than the spec-compliant explicit constraints, so we send explicit
   *    constraints with sensible defaults that work on every device class.
   *  • `audio` constraints request echo cancellation + noise suppression
   *    (Safari otherwise leaves them off, which produces feedback howl
   *    when both peers are on-speaker).
   *  • `video.facingMode: 'user'` ensures the front camera on mobile.
   */
  async addLocalMedia({ video = true, audio = true } = {}) {
    const constraints = {};
    if (audio) {
      constraints.audio = {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      };
    }
    if (video) {
      constraints.video = {
        width: { ideal: 1280, max: 1920 },
        height: { ideal: 720, max: 1080 },
        facingMode: 'user',
        frameRate: { ideal: 30, max: 30 },
      };
    }
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia(constraints);
    } catch (e) {
      // Fall back to bare constraints — some older devices reject the
      // explicit object even when the requested values are within their
      // capability set. iOS 13.x is a known offender.
      stream = await navigator.mediaDevices.getUserMedia({ video, audio });
    }
    this.localStream = stream;
    stream.getTracks().forEach((t) => this.pc.addTrack(t, stream));
    return stream;
  }

  /**
   * Attach an already-acquired MediaStream (shared across multiple
   * PeerConnections — host's case when N guests are connected and each
   * needs its own PC). Tracks are added to this PC and `localStream` is
   * stored so toggle/close behave identically to addLocalMedia().
   */
  attachLocalStream(stream) {
    if (!stream) return;
    this.localStream = stream;
    stream.getTracks().forEach((t) => {
      // Avoid double-adding the same track to this PC's senders.
      const existing = this.pc.getSenders().find((s) => s.track === t);
      if (!existing) this.pc.addTrack(t, stream);
    });
  }

  toggleAudio(enabled) {
    if (!this.localStream) return;
    this.localStream.getAudioTracks().forEach((t) => { t.enabled = enabled; });
  }

  toggleVideo(enabled) {
    if (!this.localStream) return;
    this.localStream.getVideoTracks().forEach((t) => { t.enabled = enabled; });
  }

  async startScreenShare() {
    if (this.screenStream) return this.screenStream;
    const display = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: false });
    this.screenStream = display;
    display.getTracks().forEach((t) => {
      const sender = this.pc.addTrack(t, display);
      this._screenSenders.push(sender);
      // Auto-stop when user clicks browser's "Stop sharing" UI
      t.addEventListener('ended', () => this.stopScreenShare());
    });
    return display;
  }

  async stopScreenShare() {
    if (!this.screenStream) return;
    this.screenStream.getTracks().forEach((t) => t.stop());
    this._screenSenders.forEach((s) => {
      try { this.pc.removeTrack(s); } catch (e) { /* */ }
    });
    this._screenSenders = [];
    this.screenStream = null;
  }

  /**
   * Multi-guest variant: attach a screen-share stream that was acquired by
   * the caller (one displayMedia capture, shared across N PeerConnections).
   *
   * The PeerConnection only manages its own senders here — it does NOT own
   * the MediaStream's lifecycle (caller is responsible for stopping tracks).
   * `negotiationneeded` will fire automatically and the standard offer flow
   * renegotiates the m-line for this peer.
   *
   * No-op if a screen stream is already attached to this PC.
   */
  attachScreenStream(stream) {
    if (!stream) return;
    if (this.screenStream) return;
    this.screenStream = stream;
    stream.getVideoTracks().forEach((t) => {
      const sender = this.pc.addTrack(t, stream);
      this._screenSenders.push(sender);
    });
  }

  /**
   * Reverse of attachScreenStream — removes this PC's screen senders so the
   * peer renegotiates back to camera-only. Does NOT stop the underlying
   * MediaStream (caller decides when to release the displayMedia capture).
   */
  detachScreenStream() {
    if (!this._screenSenders.length && !this.screenStream) return;
    this._screenSenders.forEach((s) => {
      try { this.pc.removeTrack(s); } catch (e) { /* */ }
    });
    this._screenSenders = [];
    this.screenStream = null;
  }

  // ─────────────────────────── Signaling helpers ───────────────────────────
  async _makeOffer() {
    const offer = await this.pc.createOffer();
    await this.pc.setLocalDescription(offer);
    // eslint-disable-next-line no-console
    console.log('[WebRTC] sent offer', { sdpType: this.pc.localDescription?.type, signalingState: this.pc.signalingState });
    this.onSignal({ type: 'offer', payload: this.pc.localDescription });
  }

  /** Public — manually trigger initial offer (host calls once both peers ready). */
  async start() {
    if (!this.isOfferer) return;
    // From this point on, future renegotiations (screen share, ICE restart)
    // are allowed to drive negotiationneeded → _makeOffer automatically.
    this._allowAutoNegotiate = true;
    // Use the same guards as `negotiationneeded` so we never produce a
    // duplicate offer that would corrupt m-line ordering on the peer.
    if (this._makingOffer) return;
    if (this.pc.signalingState !== 'stable') return;
    try {
      this._makingOffer = true;
      await this._makeOffer();
    } finally {
      this._makingOffer = false;
    }
  }

  /**
   * Trigger an ICE restart — used after a network drop / WS reconnect to
   * re-establish media without tearing down the whole RTCPeerConnection.
   * Only the offerer kicks this off; the answer flows back via the standard
   * offer/answer path.
   */
  async restartIce() {
    if (!this.isOfferer) return;
    if (this._makingOffer) return;
    try {
      this._makingOffer = true;
      // Spec-blessed path (Chrome/Edge/Firefox):
      if (typeof this.pc.restartIce === 'function') {
        this.pc.restartIce();
        // restartIce() flips a flag; the actual offer is created by
        // negotiationneeded firing immediately after.
      } else {
        // Safari fallback — pass {iceRestart:true} to createOffer
        const offer = await this.pc.createOffer({ iceRestart: true });
        await this.pc.setLocalDescription(offer);
        this.onSignal({ type: 'offer', payload: this.pc.localDescription });
      }
    } finally {
      this._makingOffer = false;
    }
  }

  async handleOffer(offer) {
    // Perfect-negotiation collision detection: if we're already mid-offer or
    // not in 'stable' state, treat the incoming offer as a glare.
    const offerCollision = this._makingOffer || this.pc.signalingState !== 'stable';
    this._ignoreOffer = !this._isPolite && offerCollision;
    if (this._ignoreOffer) {
      // Impolite peer (host) ignores the colliding offer — its own will win.
      return;
    }
    if (offerCollision) {
      // Polite peer (guest) rolls back its pending offer to accept the
      // incoming one. Both setLocalDescription({type:'rollback'}) +
      // setRemoteDescription must happen before createAnswer.
      try {
        await Promise.all([
          this.pc.setLocalDescription({ type: 'rollback' }),
          this.pc.setRemoteDescription(new RTCSessionDescription(offer)),
        ]);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('rollback during offer collision failed', e);
        return;
      }
    } else {
      await this.pc.setRemoteDescription(new RTCSessionDescription(offer));
    }
    const answer = await this.pc.createAnswer();
    await this.pc.setLocalDescription(answer);
    // eslint-disable-next-line no-console
    console.log('[WebRTC] sent answer', { signalingState: this.pc.signalingState });
    this.onSignal({ type: 'answer', payload: this.pc.localDescription });
  }

  async handleAnswer(answer) {
    // Spurious answers (e.g. arriving after rollback) are non-fatal — only
    // apply when we're actually waiting for one.
    if (this.pc.signalingState !== 'have-local-offer') {
      // eslint-disable-next-line no-console
      console.warn('Ignoring spurious answer; signalingState=', this.pc.signalingState);
      return;
    }
    await this.pc.setRemoteDescription(new RTCSessionDescription(answer));
    // eslint-disable-next-line no-console
    console.log('[WebRTC] applied answer', { signalingState: this.pc.signalingState });
  }

  async handleIce(candidate) {
    try {
      await this.pc.addIceCandidate(new RTCIceCandidate(candidate));
    } catch (e) {
      // ICE candidate races / closed connections are non-fatal; if we
      // intentionally ignored an offer, we should also ignore its ICE.
      if (!this._ignoreOffer) {
        // eslint-disable-next-line no-console
        console.warn('addIceCandidate failed', e);
      }
    }
  }

  /**
   * Log the WebRTC ICE candidate pair the connection actually settled on.
   *
   * If `candidateType === 'relay'` it means the media is flowing via TURN
   * (the only thing that works for cellular ↔ WiFi without UPnP). Anything
   * else (`host` / `srflx` / `prflx`) is direct-ish.
   *
   * Output appears in the browser console as `[WebRTC] selected pair: ...`
   * so the broker can copy/paste it back to us when calls fail.
   */
  async _logSelectedIceCandidate() {
    if (!this.pc || typeof this.pc.getStats !== 'function') return;
    try {
      const stats = await this.pc.getStats();
      let pair = null;
      let local = null;
      let remote = null;
      stats.forEach((s) => {
        if (s.type === 'candidate-pair' && s.state === 'succeeded' && s.nominated) pair = s;
      });
      if (!pair) return;
      stats.forEach((s) => {
        if (s.id === pair.localCandidateId) local = s;
        if (s.id === pair.remoteCandidateId) remote = s;
      });
      const localType = local?.candidateType || '?';
      const remoteType = remote?.candidateType || '?';
      const protocol = local?.protocol || remote?.protocol || '?';
      // eslint-disable-next-line no-console
      console.log(`[WebRTC] selected pair: local=${localType} remote=${remoteType} proto=${protocol}` + (localType === 'relay' || remoteType === 'relay' ? ' (TURN relay in use)' : ''));
      if (typeof this._onIceTypeKnown === 'function') {
        this._onIceTypeKnown({ local: localType, remote: remoteType, protocol });
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[WebRTC] could not read getStats', e);
    }
  }

  close() {
    try { this.localStream?.getTracks().forEach((t) => t.stop()); } catch (e) { /* */ }
    try { this.screenStream?.getTracks().forEach((t) => t.stop()); } catch (e) { /* */ }
    try { this.pc.close(); } catch (e) { /* */ }
  }
}
